import React from "react";
import { Dialog } from "../../packages/excalidraw/components/Dialog";
import DialogActionButton from "../../packages/excalidraw/components/DialogActionButton";
import type { UnsavedWorkKind } from "../data/unsavedWork";

/**
 * The guard in front of "New mindmap" when the canvas holds work that is not
 * safely in the cloud. Starting a new mindmap resets the scene, so without this
 * the click silently destroyed the drawing (no confirm, no undo).
 *
 * Three ways out, in the order a user wants them:
 *   Save it first  — run the Save-to-cloud flow (or flush the pending autosave),
 *                    then start the new mindmap.
 *   Discard        — accept the loss, explicitly.
 *   Cancel         — the safe default (also what Escape / the backdrop do).
 */
export const NewMindmapConfirmDialog: React.FC<{
  kind: UnsavedWorkKind;
  /** Title of the open cloud file, when that's what's at risk. */
  fileTitle?: string;
  /**
   * Omitted when no save would actually rescue the work at risk (no session, or
   * `scratchBehindCloudFile`) — then only discard/cancel.
   */
  /** May be async — creating the file IS the save when there is no row yet. */
  onSaveFirst?: () => void | Promise<void>;
  onDiscard: () => void;
  onCancel: () => void;
}> = ({ kind, fileTitle, onSaveFirst, onDiscard, onCancel }) => {
  let body: React.ReactNode;
  if (kind === "cloudFile") {
    body = (
      <>
        Your latest changes to <strong>{fileTitle || "this mindmap"}</strong>{" "}
        haven&apos;t reached the cloud yet. Starting a new mindmap now leaves
        them behind.
      </>
    );
  } else if (kind === "scratchBehindCloudFile") {
    // The open cloud file IS saved — the loss is the scratch scene behind it, and
    // no save rescues it, so callers pass no onSaveFirst here.
    body = (
      <>
        You have an unsaved mindmap from earlier on your scratch canvas.
        Starting a new one will discard it.
      </>
    );
  } else {
    body = (
      <>
        This canvas has unsaved work. It isn&apos;t in My Files yet, so starting
        a new mindmap will discard it.
      </>
    );
  }

  return (
    <Dialog
      size="small"
      onCloseRequest={onCancel}
      title="Start a new mindmap?"
      className="new-mindmap-confirm-dialog"
    >
      <div
        data-testid="new-mindmap-confirm-dialog"
        style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}
      >
        <p style={{ margin: 0, lineHeight: 1.5 }}>{body}</p>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: "0.75rem",
          }}
        >
          <DialogActionButton
            label="Cancel"
            data-testid="new-mindmap-cancel"
            onClick={onCancel}
          />
          <DialogActionButton
            label="Discard and start new"
            data-testid="new-mindmap-discard"
            actionType="danger"
            onClick={onDiscard}
          />
          {onSaveFirst && (
            <DialogActionButton
              label="Save it first"
              data-testid="new-mindmap-save-first"
              actionType="primary"
              onClick={() => void onSaveFirst()}
            />
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default NewMindmapConfirmDialog;
